import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Card,
  Dialog,
  ErrorState,
  Field,
  Input,
  LoadingState,
  useToast,
} from '@/components/ui';
import { useSession } from '@/features/auth/session-context';
import {
  useCalendarActions,
  useHours,
  useOutsideHours,
  type BranchHours,
  type OutsideHours,
} from '@/features/calendar/api';
import { isApiError } from '@/lib/api';
import styles from './calendar.module.css';
import { CLINIC_TZ } from '@/lib/clinic-time';

/** "Mon 2 Nov, 20:30" in the branch's time zone (review fix). */
const formatWhen = (iso: string, timeZone = CLINIC_TZ) =>
  new Date(iso).toLocaleString('en-GB', {
    timeZone,
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

/** "Opening hours" (#21): each branch's hours, the Friday prayer break and Ramadan sessions. */
export function HoursTab() {
  const { hasPermission } = useSession();
  const { data, isLoading, error, refetch } = useHours();
  const [editing, setEditing] = useState<BranchHours | null>(null);
  const [asking, setAsking] = useState<{
    branch: BranchHours;
    outside: OutsideHours;
  } | null>(null);
  const canManage = hasPermission('calendar:manage');
  return (
    <div className={styles.stack}>
      {isLoading && <LoadingState />}
      {error && <ErrorState error={error} onRetry={() => void refetch()} />}
      <div className={styles.hoursGrid}>
        {data?.map((b) => (
          <Card
            key={b.branch.id}
            title={b.branch.name}
            actions={
              hasPermission('calendar:manage') && (
                <Button size="sm" onClick={() => setEditing(b)}>
                  Edit
                </Button>
              )
            }
          >
            {canManage && (
              <OutsideBanner
                b={b}
                onAsk={(outside) => setAsking({ branch: b, outside })}
              />
            )}
            {b.hours ? (
              <dl className={styles.rows}>
                <dt>Daily</dt>
                <dd>
                  {b.hours.opens} to {b.hours.closes}
                </dd>
                <dt>Friday prayers</dt>
                <dd>
                  {b.hours.fridayBreakStart
                    ? `Closed ${b.hours.fridayBreakStart} to ${b.hours.fridayBreakEnd}; no bookings in this window`
                    : 'No break set'}
                </dd>
                <dt>Ramadan</dt>
                <dd>
                  {b.hours.ramadanFirst
                    ? `${b.hours.ramadanFirst}${b.hours.ramadanSecond ? `, then ${b.hours.ramadanSecond} (break for iftar)` : ''}`
                    : 'Not set'}
                </dd>
              </dl>
            ) : (
              <p className={styles.warn}>
                No opening hours yet: bookings are not limited.
              </p>
            )}
          </Card>
        ))}
      </div>
      <p className={styles.note}>
        The app and the booking widget only offer slots inside these hours.
        Public holidays, closures and doctor leave on the calendar block slots
        too, and patients already booked are told straight away.
      </p>
      <EditHours branch={editing} onClose={() => setEditing(null)} />
      <AskRebook asking={asking} onClose={() => setAsking(null)} />
    </div>
  );
}

function EditHours({
  branch,
  onClose,
}: {
  branch: BranchHours | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  // RL-1663: after saving, the bookings the new hours leave out.
  const [outside, setOutside] = useState<OutsideHours | null>(null);
  const close = () => {
    setOutside(null);
    onClose();
  };
  return (
    <Dialog
      open={!!branch}
      onClose={close}
      title={
        outside
          ? t('calendarHours.title', { count: outside.count })
          : `Opening hours · ${branch?.branch.name ?? ''}`
      }
    >
      {branch && outside && (
        <OutsideHoursPrompt
          branchId={branch.branch.id}
          outside={outside}
          onDone={close}
        />
      )}
      {branch && !outside && (
        <HoursForm
          key={branch.branch.id}
          b={branch}
          onDone={(saved) =>
            saved?.outsideHours?.count
              ? setOutside(saved.outsideHours)
              : close()
          }
        />
      )}
    </Dialog>
  );
}

/**
 * RL-1663 review: while bookings are outside the hours (asked "Not now", or
 * changed elsewhere), the branch card says so and offers to ask them.
 */
function OutsideBanner({
  b,
  onAsk,
}: {
  b: BranchHours;
  onAsk: (outside: OutsideHours) => void;
}) {
  const { t } = useTranslation();
  const { data } = useOutsideHours(b.branch.id, !!b.hours);
  if (!data?.count) return null;
  return (
    <div className={styles.warn} role="status">
      {t('calendarHours.banner', { count: data.count })}{' '}
      <Button size="sm" onClick={() => onAsk(data)}>
        {t('calendarHours.ask')}
      </Button>
    </div>
  );
}

function AskRebook({
  asking,
  onClose,
}: {
  asking: { branch: BranchHours; outside: OutsideHours } | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Dialog
      open={!!asking}
      onClose={onClose}
      title={t('calendarHours.title', { count: asking?.outside.count ?? 0 })}
    >
      {asking && (
        <OutsideHoursPrompt
          branchId={asking.branch.branch.id}
          outside={asking.outside}
          onDone={onClose}
        />
      )}
    </Dialog>
  );
}

/** How many bookings the prompt lists by name before "and N more". */
const LISTED = 5;

/**
 * RL-1663 (console item 6): "3 bookings fall outside the new hours. Ask them
 * to rebook?" Nothing is sent until `Ask them to rebook`; each patient then
 * gets "Your booking needs a new time" with `Pick a new time` in the app.
 */
function OutsideHoursPrompt({
  branchId,
  outside,
  onDone,
}: {
  branchId: string;
  outside: OutsideHours;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const a = useCalendarActions();
  const toast = useToast();
  const more = outside.count - Math.min(outside.count, LISTED);
  return (
    <div className={styles.stack}>
      <p className={styles.warn}>
        {t('calendarHours.body', { count: outside.count })}
      </p>
      <ul className={styles.note}>
        {outside.bookings.slice(0, LISTED).map((b) => (
          <li key={b.appointmentId}>
            {formatWhen(b.startsAt, outside.timezone)} · {b.patient.fullName} ·{' '}
            {b.service.name}
            {b.reason === 'FRIDAY_PRAYERS'
              ? ` (${t('calendarHours.friday')})`
              : ''}
          </li>
        ))}
        {more > 0 && <li>{t('calendarHours.more', { count: more })}</li>}
      </ul>
      <div className={styles.actions}>
        <Button onClick={onDone}>{t('calendarHours.notNow')}</Button>
        <Button
          variant="primary"
          loading={a.rebookOutsideHours.isPending}
          onClick={() =>
            void a.rebookOutsideHours
              .mutateAsync(branchId)
              .then((r) => {
                toast.show({
                  tone: 'success',
                  title: t('calendarHours.sent', { count: r.asked }),
                  body: t('calendarHours.sentBody'),
                });
                onDone();
              })
              .catch((err: unknown) =>
                toast.show({
                  tone: 'error',
                  title: t('calendarHours.notSent'),
                  body: isApiError(err) ? err.message : undefined,
                  error: err,
                }),
              )
          }
        >
          {t('calendarHours.ask')}
        </Button>
      </div>
    </div>
  );
}

function HoursForm({
  b,
  onDone,
}: {
  b: BranchHours;
  onDone: (saved?: BranchHours) => void;
}) {
  const a = useCalendarActions();
  const toast = useToast();
  const h = b.hours;
  const [opens, setOpens] = useState(h?.opens ?? '08:00');
  const [closes, setCloses] = useState(h?.closes ?? '22:00');
  const [fs, setFs] = useState(h?.fridayBreakStart ?? '');
  const [fe, setFe] = useState(h?.fridayBreakEnd ?? '');
  const [r1, setR1] = useState(h?.ramadanFirst ?? '');
  const [r2, setR2] = useState(h?.ramadanSecond ?? '');
  const range = /^\d{2}:\d{2} to \d{2}:\d{2}$/;
  const invalid =
    (r1.trim() !== '' && !range.test(r1.trim())) ||
    (r2.trim() !== '' && !range.test(r2.trim())) ||
    (fs === '') !== (fe === '');
  return (
    <form
      className={styles.stack}
      onSubmit={(e) => {
        e.preventDefault();
        void a.saveHours
          .mutateAsync({
            branchId: b.branch.id,
            opens,
            closes,
            fridayBreakStart: fs || null,
            fridayBreakEnd: fe || null,
            ramadanFirst: r1.trim() || null,
            ramadanSecond: r2.trim() || null,
          })
          .then((saved) => {
            toast.show({ tone: 'success', title: 'Opening hours saved' });
            onDone(saved);
          })
          .catch((err: unknown) =>
            toast.show({
              tone: 'error',
              title: 'Not saved',
              body: isApiError(err) ? err.message : undefined,
              error: err,
            }),
          );
      }}
    >
      <Field label="Opens">
        <Input
          type="time"
          value={opens}
          onChange={(e) => setOpens(e.target.value)}
        />
      </Field>
      <Field label="Closes">
        <Input
          type="time"
          value={closes}
          onChange={(e) => setCloses(e.target.value)}
        />
      </Field>
      <Field label="Friday break starts (optional)">
        <Input type="time" value={fs} onChange={(e) => setFs(e.target.value)} />
      </Field>
      <Field label="Friday break ends (optional)">
        <Input type="time" value={fe} onChange={(e) => setFe(e.target.value)} />
      </Field>
      <Field
        label="Ramadan: first session (optional)"
        hint="e.g. 08:00 to 18:30"
      >
        <Input value={r1} onChange={(e) => setR1(e.target.value)} />
      </Field>
      <Field label="Ramadan: after iftar (optional)" hint="e.g. 20:30 to 22:00">
        <Input value={r2} onChange={(e) => setR2(e.target.value)} />
      </Field>
      <div className={styles.actions}>
        <Button onClick={() => onDone()}>Cancel</Button>
        <Button
          type="submit"
          variant="primary"
          disabled={invalid}
          loading={a.saveHours.isPending}
        >
          Save
        </Button>
      </div>
    </form>
  );
}
